import { twMerge } from "tailwind-merge";
import { TimezoneOption } from "@core/features/timezones";
import { ContiguousTimeslot, Timeslot } from "@core/features/timeslots";
import type { SchedulingMode } from "@core/features/actions";
import type { ProcessedSelectableOption } from "@core/features/scheduling";
import { SchedulingAvailabilityGrid } from "../SchedulingAvailabilityGrid";
import { SchedulingInstructions } from "../SchedulingInstructions";
import { SchedulingRequirements } from "../SchedulingRequirements";
import { SchedulingTimezones } from "../SchedulingTimezones";

export type Props = {
  isLoading: boolean;
  isSubmitting: boolean;
  timeSlots: Timeslot[];
  selectedTimeSlots: Timeslot[];
  contiguousTimeslots: ContiguousTimeslot[];
  handleMouseDown: (e: React.MouseEvent, slot: Timeslot) => void;
  handleMouseOver: (e: React.MouseEvent, slot: Timeslot) => void;
  handleTouchStart: (e: React.TouchEvent, slot: Timeslot) => void;
  handleTouchEnd: (e: React.TouchEvent, slot: Timeslot) => void;
  handleTouchMove: (e: React.TouchEvent, slot: Timeslot) => void;
  validHoursCount: number;
  timeZoneOptions: TimezoneOption[];
  selectedTimezone: TimezoneOption;
  onTimezoneChange: (val: TimezoneOption | null) => void;
  /** Selection mode */
  mode: SchedulingMode;
  /** Array of pre-formatted header strings with timezone abbreviation (e.g., "Jan 14 PST") */
  headers: string[];
  /** Number of columns to render */
  columnCount: number;
  /** Selectable options for 'select-options' mode (processed with required id/day) */
  selectableOptions: ProcessedSelectableOption[];
  /** IDs of currently selected options */
  selectedOptionIds: string[];
  /** Minimum required hours for freeform mode */
  minRequiredHours: number;
  /** Minimum contiguous duration for freeform mode */
  minContiguousDuration?: number;
  /** Number of options user must select for select-options and repeating modes */
  requiredSelections: number;
  /** Optional deadline for editing selections */
  deadline?: string;
  /** When true, disables all interaction */
  isReadOnly?: boolean;
};

export function Scheduling({
  isLoading,
  isSubmitting,
  timeSlots,
  selectedTimeSlots,
  contiguousTimeslots,
  handleMouseDown,
  handleMouseOver,
  handleTouchStart,
  handleTouchMove,
  handleTouchEnd,
  validHoursCount,
  timeZoneOptions,
  selectedTimezone,
  onTimezoneChange,
  mode,
  headers,
  columnCount,
  selectableOptions,
  selectedOptionIds,
  minRequiredHours,
  minContiguousDuration = 2,
  requiredSelections,
  deadline,
  isReadOnly = false,
}: Props) {
  // Determine if we're in an options-based mode
  const isOptionsMode = mode === "select-options" || mode === "repeating";

  // Calculate selected count based on mode
  // For repeating mode, count unique time ranges (selectedOptionIds.length / columnCount)
  const selectedCount =
    mode === "repeating"
      ? Math.ceil(selectedOptionIds.length / columnCount)
      : selectedOptionIds.length;

  return (
    <div
      className={twMerge(
        "flex flex-col gap-4 md:flex-row",
        isSubmitting && "opacity-20 pointer-events-none",
        isReadOnly && "pointer-events-none"
      )}
    >
      <SchedulingAvailabilityGrid
        className="flex-1"
        isLoading={isLoading}
        timeSlots={timeSlots}
        selectedTimeSlots={selectedTimeSlots}
        contiguousTimeslots={contiguousTimeslots}
        handleMouseDown={handleMouseDown}
        handleMouseOver={handleMouseOver}
        handleTouchStart={handleTouchStart}
        handleTouchMove={handleTouchMove}
        handleTouchEnd={handleTouchEnd}
        headers={headers}
        columnCount={columnCount}
        selectableOptions={selectableOptions}
        selectedOptionIds={selectedOptionIds}
        isOptionsMode={isOptionsMode}
        mode={mode}
      />
      <div className="flex flex-col gap-4 w-full flex-1 md:flex-none md:max-w-xs md:min-w-xs">
        <SchedulingTimezones
          options={timeZoneOptions}
          value={selectedTimezone.value}
          onChange={onTimezoneChange}
        />
        <SchedulingInstructions
          mode={mode}
          minRequiredHours={minRequiredHours}
          minContiguousDuration={minContiguousDuration}
          requiredSelections={requiredSelections}
          deadline={deadline}
        />
        <SchedulingRequirements
          mode={mode}
          validHoursCount={validHoursCount}
          minRequiredHours={minRequiredHours}
          selectedCount={selectedCount}
          requiredSelections={requiredSelections}
        />
      </div>
    </div>
  );
}

export default Scheduling;
